import { css } from 'styled-components';
/**
 * @description
 * Typography scale from the Figma design system, see
 * https://www.figma.com/design/oyaJgq8UP0AHSRWuHNPx3b/Deluge---Design-System?node-id=2763-8241
 *
 * Each named style exposes `font-size`, `line-height` and `font-weight` so
 * components can compose a style without hardcoding its individual values:
 *
 * font-size: var(--typography-body-1-regular-font-size);
 * line-height: var(--typography-body-1-regular-line-height);
 * font-weight: var(--typography-body-1-regular-font-weight);
 */
export const typographyVariablesCss = css `
  --font-weight-regular: 400;
  --font-weight-medium: 500;
  --font-weight-semi-bold: 600;
  --font-weight-bold: 700;

  /* Headlines */
  --typography-h3-font-size: 30px;
  --typography-h3-line-height: 1.15;
  --typography-h3-font-weight: var(--font-weight-bold);

  --typography-h4-font-size: 20px;
  --typography-h4-line-height: 1.15;
  --typography-h4-font-weight: var(--font-weight-bold);

  /* Body 1 */
  --typography-body-1-regular-font-size: 16px;
  --typography-body-1-regular-line-height: 20px;
  --typography-body-1-regular-font-weight: var(--font-weight-regular);

  --typography-body-1-medium-font-size: 16px;
  --typography-body-1-medium-line-height: 20px;
  --typography-body-1-medium-font-weight: var(--font-weight-medium);

  --typography-body-1-semi-bold-font-size: 16px;
  --typography-body-1-semi-bold-line-height: 20px;
  --typography-body-1-semi-bold-font-weight: var(--font-weight-semi-bold);

  --typography-body-1-bold-font-size: 16px;
  --typography-body-1-bold-line-height: 20px;
  --typography-body-1-bold-font-weight: var(--font-weight-bold);

  /* Body 2 */
  --typography-body-2-regular-font-size: 14px;
  --typography-body-2-regular-line-height: 20px;
  --typography-body-2-regular-font-weight: var(--font-weight-regular);

  --typography-body-2-medium-font-size: 14px;
  --typography-body-2-medium-line-height: 20px;
  --typography-body-2-medium-font-weight: var(--font-weight-medium);

  --typography-body-2-semi-bold-font-size: 14px;
  --typography-body-2-semi-bold-line-height: 20px;
  --typography-body-2-semi-bold-font-weight: var(--font-weight-semi-bold);

  --typography-body-2-bold-font-size: 14px;
  --typography-body-2-bold-line-height: 20px;
  --typography-body-2-bold-font-weight: var(--font-weight-bold);

  /* Body 3 */
  --typography-body-3-regular-font-size: 13px;
  --typography-body-3-regular-line-height: 16px;
  --typography-body-3-regular-font-weight: var(--font-weight-regular);

  --typography-body-3-medium-font-size: 13px;
  --typography-body-3-medium-line-height: 16px;
  --typography-body-3-medium-font-weight: var(--font-weight-medium);

  --typography-body-3-bold-font-size: 13px;
  --typography-body-3-bold-line-height: 16px;
  --typography-body-3-bold-font-weight: var(--font-weight-bold);

  /* Body 4 */
  --typography-body-4-regular-font-size: 12px;
  --typography-body-4-regular-line-height: 16px;
  --typography-body-4-regular-font-weight: var(--font-weight-regular);

  --typography-body-4-medium-font-size: 12px;
  --typography-body-4-medium-line-height: 16px;
  --typography-body-4-medium-font-weight: var(--font-weight-medium);

  --typography-body-4-semi-bold-font-size: 12px;
  --typography-body-4-semi-bold-line-height: 16px;
  --typography-body-4-semi-bold-font-weight: var(--font-weight-semi-bold);

  --typography-body-4-bold-font-size: 12px;
  --typography-body-4-bold-line-height: 16px;
  --typography-body-4-bold-font-weight: var(--font-weight-bold);

  /* Body 5 */
  --typography-body-5-regular-font-size: 11px;
  --typography-body-5-regular-line-height: 12px;
  --typography-body-5-regular-font-weight: var(--font-weight-regular);

  --typography-body-5-medium-font-size: 11px;
  --typography-body-5-medium-line-height: 12px;
  --typography-body-5-medium-font-weight: var(--font-weight-medium);

  --typography-body-5-semi-bold-font-size: 11px;
  --typography-body-5-semi-bold-line-height: 12px;
  --typography-body-5-semi-bold-font-weight: var(--font-weight-semi-bold);

  --typography-body-5-bold-font-size: 11px;
  --typography-body-5-bold-line-height: 12px;
  --typography-body-5-bold-font-weight: var(--font-weight-bold);

  /* Description */
  --typography-description-medium-font-size: 10px;
  --typography-description-medium-line-height: 12px;
  --typography-description-medium-font-weight: var(--font-weight-medium);

  --typography-description-semi-bold-font-size: 10px;
  --typography-description-semi-bold-line-height: 12px;
  --typography-description-semi-bold-font-weight: var(--font-weight-semi-bold);

  --typography-description-bold-font-size: 10px;
  --typography-description-bold-line-height: 12px;
  --typography-description-bold-font-weight: var(--font-weight-bold);

  /* Mobile
   * https://www.figma.com/design/oyaJgq8UP0AHSRWuHNPx3b/Deluge---Design-System?node-id=12269-8806
   */
  --typography-mobile-30-semi-bold-font-size: 30px;
  /* Figma specifies 16px here — tighter than the font size. Kept as designed. */
  --typography-mobile-30-semi-bold-line-height: 16px;
  --typography-mobile-30-semi-bold-font-weight: var(--font-weight-semi-bold);

  --typography-mobile-24-bold-font-size: 24px;
  --typography-mobile-24-bold-line-height: 1.2;
  --typography-mobile-24-bold-font-weight: var(--font-weight-bold);

  --typography-mobile-18-bold-font-size: 18px;
  --typography-mobile-18-bold-line-height: 1.4;
  --typography-mobile-18-bold-font-weight: var(--font-weight-bold);

  --typography-mobile-16-bold-font-size: 16px;
  --typography-mobile-16-bold-line-height: 20px;
  --typography-mobile-16-bold-font-weight: var(--font-weight-bold);

  --typography-mobile-16-semi-bold-font-size: 16px;
  --typography-mobile-16-semi-bold-line-height: 16px;
  --typography-mobile-16-semi-bold-font-weight: var(--font-weight-semi-bold);

  --typography-mobile-14-semi-bold-font-size: 14px;
  --typography-mobile-14-semi-bold-line-height: 20px;
  --typography-mobile-14-semi-bold-font-weight: var(--font-weight-semi-bold);

  --typography-mobile-14-regular-font-size: 14px;
  --typography-mobile-14-regular-line-height: 20px;
  --typography-mobile-14-regular-font-weight: var(--font-weight-regular);

  --typography-mobile-13-semi-bold-font-size: 13px;
  --typography-mobile-13-semi-bold-line-height: 20px;
  --typography-mobile-13-semi-bold-font-weight: var(--font-weight-semi-bold);

  --typography-mobile-13-medium-font-size: 13px;
  --typography-mobile-13-medium-line-height: 20px;
  --typography-mobile-13-medium-font-weight: var(--font-weight-medium);

  --typography-mobile-13-regular-font-size: 13px;
  --typography-mobile-13-regular-line-height: 16px;
  --typography-mobile-13-regular-font-weight: var(--font-weight-regular);

  --typography-mobile-12-bold-font-size: 12px;
  --typography-mobile-12-bold-line-height: 16px;
  --typography-mobile-12-bold-font-weight: var(--font-weight-bold);

  --typography-mobile-12-semi-bold-font-size: 12px;
  --typography-mobile-12-semi-bold-line-height: 16px;
  --typography-mobile-12-semi-bold-font-weight: var(--font-weight-semi-bold);

  --typography-mobile-12-medium-font-size: 12px;
  --typography-mobile-12-medium-line-height: 16px;
  --typography-mobile-12-medium-font-weight: var(--font-weight-medium);

  --typography-mobile-12-regular-font-size: 12px;
  --typography-mobile-12-regular-line-height: 16px;
  --typography-mobile-12-regular-font-weight: var(--font-weight-regular);

  --typography-mobile-10-regular-font-size: 10px;
  --typography-mobile-10-regular-line-height: 14px;
  --typography-mobile-10-regular-font-weight: var(--font-weight-regular);

  /* Buttons */
  --typography-button-l-font-size: 20px;
  --typography-button-l-line-height: 1;
  --typography-button-l-font-weight: var(--font-weight-bold);

  --typography-button-m-font-size: 14px;
  --typography-button-m-line-height: 1;
  --typography-button-m-font-weight: var(--font-weight-bold);

  --typography-button-xs-s-font-size: 12px;
  --typography-button-xs-s-line-height: 1;
  --typography-button-xs-s-font-weight: var(--font-weight-bold);
`;
//# sourceMappingURL=typography-variables.js.map